import React from 'react';
import { useQuery } from 'react-query';
import { Editor } from '@splashthat/splash-cms-engine';
import { SuiSpinner } from '@splashthat/sui';
import { getInstance as getBootstrap } from 'utils/bootstrap';
import { getLibrarianApiClient } from 'lib/SplashApiClient/subclients/LibrarianApiClient';

import { BLOCKS } from 'common/components/TouchpointEditor/constants/blocks';
import { WebRenderer } from 'common/components/TouchpointEditor/renderer/WebRenderer/WebRenderer';
import { TouchpointDataProvider } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { LoadingSpinnerContainer } from 'common/components/TouchpointEditor/components/TouchpointContainer';
import { CONFIRMATIONS } from 'common/components/TouchpointEditor/constants/touchpoints';

import { confirmationsDefaultStructures } from 'common/compounds/ConfirmationsTouchpoint/constants/defaultStructures';
import {
    RSVP_YES_DOI_MESSAGE_DISPLAY_NAME,
    createSessionSummaryNode,
} from 'common/compounds/ConfirmationsTouchpoint/constants/defaultStructures/rsvpYes';
import { addDoubleOptInMessage } from 'common/compounds/ConfirmationsTouchpoint/ConfirmationsTouchpoint';
import Portal from './Portal';

const bootstrap = getBootstrap();
const eventId = bootstrap.get('splashPage.eventId');
const storedConfirmationsTemplates = bootstrap.get('onPageConfirmations.states', []);
const isDoubleOptInEnabled = bootstrap.get('isDoubleOptInEnabled', false);
const isDoubleOptInFlagEnabled = bootstrap.get('flags.enabledDoubleOptIn', false);

const setNodeVisibility = (node, visible) => ({
    ...node,
    props: { ...node.props, meta: { ...node.props.meta, visible } },
});

// When sessions are registered, ensure SessionsSummary is visible and the event-level
// CalendarAttachment is hidden so guests see per-session calendar links instead.
// Returns a new tree object — the original is never mutated.
export const adjustTreeForSessionRegistrations = (tree, sessionRegistrationConfirmation) => {
    const {
        sessionsRegistered = [],
        sessionsWaitlisted = [],
        sessionsSkipped = [],
    } = sessionRegistrationConfirmation ?? {};
    const hasSessionData = sessionsRegistered.length || sessionsWaitlisted.length || sessionsSkipped.length;

    // Skip adjustment when there are no session registrations (standard event RSVP)
    // or when the tree hasn't loaded yet.
    if (!hasSessionData || !tree) return tree;

    // Step 1: apply visibility changes to every node immutably.
    // - SessionsSummary must be visible so guests see per-session titles and calendar links.
    // - The event-level CalendarAttachment must be hidden to avoid a duplicate "Add to Calendar"
    //   section above the per-session ones rendered by SessionsSummary.
    const treeWithVisibility = Object.fromEntries(
        Object.entries(tree).map(([id, node]) => {
            if (node.type === 'SessionsSummary') return [id, setNodeVisibility(node, true)];
            if (node.type === 'CalendarAttachment') return [id, setNodeVisibility(node, false)];
            return [id, node];
        }),
    );

    // Step 2: determine the summary node id — use the existing one or create and inject a new one.
    // Saved confirmation templates created before SessionsSummary was added to the default
    // structure won't have the node at all; inject one so older templates still show
    // per-session calendar links for guests who registered for sessions.
    const existingEntry = Object.entries(treeWithVisibility).find(([, node]) => node.type === 'SessionsSummary');
    const summaryId = existingEntry
        ? existingEntry[0]
        : (() => {
              const node = createSessionSummaryNode();
              return node.id;
          })();

    const treeWithSummary = existingEntry
        ? treeWithVisibility
        : (() => {
              const node = createSessionSummaryNode();
              return { ...treeWithVisibility, [node.id]: setNodeVisibility(node, true) };
          })();

    // Step 3: wire summaryId into the first Modal that doesn't already contain it.
    // Using find() so only the primary confirmation Modal is targeted — a template with
    // multiple modals would otherwise show SessionsSummary in all of them.
    const targetModalEntry = Object.entries(treeWithSummary).find(
        ([, node]) => node.type === 'Modal' && Array.isArray(node.childNodes) && !node.childNodes.includes(summaryId),
    );

    if (!targetModalEntry) return treeWithSummary;

    const [targetModalId, targetModal] = targetModalEntry;
    // Insert before the last child (typically the close button) so SessionsSummary
    // appears above it, matching the position the admin editor places it.
    const insertIndex = Math.max(targetModal.childNodes.length - 1, 0);
    const updatedChildNodes = [
        ...targetModal.childNodes.slice(0, insertIndex),
        summaryId,
        ...targetModal.childNodes.slice(insertIndex),
    ];

    return { ...treeWithSummary, [targetModalId]: { ...targetModal, childNodes: updatedChildNodes } };
};

// Function to handle the addition or visibility update of the Double Opt-In message
// in the confirmation structure
const handleDoubleOptInMessage = (components, isEnabled) => {
    const doiKey = Object.keys(components).find(
        (key) => components[key].props.meta.displayName === RSVP_YES_DOI_MESSAGE_DISPLAY_NAME,
    );

    // If DOI message exists and visibility needs to be updated, return a new object
    if (doiKey && components[doiKey].props.meta.visible !== isEnabled) {
        return {
            ...components,
            [doiKey]: {
                ...components[doiKey],
                props: {
                    ...components[doiKey].props,
                    meta: {
                        ...components[doiKey].props.meta,
                        visible: isEnabled,
                    },
                },
            },
        };
    }

    // If DOI message doesn't exist and should be enabled, create and insert it
    if (!doiKey && isEnabled) {
        return addDoubleOptInMessage(components);
    }

    // If no changes are needed, return the original components object
    return components;
};

export default function App({ serverResponse, triggerTypes } = {}) {
    const portal = React.useRef(null);
    const confirmationsApiClient = React.useRef(getLibrarianApiClient(eventId)).current;

    const { confirmation, tickets, guestAccessHash, sessionRegistrationConfirmation } = serverResponse;
    const confirmationState = triggerTypes.find((trigger) => storedConfirmationsTemplates.includes(trigger));
    const [defaultConfirmationState] = triggerTypes.slice(-1);

    const request = useQuery(
        ['confirmationStructure', confirmationState],
        () => confirmationsApiClient.getPublicEventPageConfirmationByState(confirmationState),
        {
            staleTime: Infinity,
            cacheTime: 0,
        },
    );

    const event = {
        confirmationsConfig: {
            ...(confirmation?.config || {}),
            tickets,
        },
        sessionRegistrationConfirmation,
        touchpoint: CONFIRMATIONS,
    };
    event.id = eventId;

    const guest = {
        guestAccessHash,
    };

    const baseTree = request?.data || confirmationsDefaultStructures[defaultConfirmationState];
    const effectiveConfirmationState = request?.data ? confirmationState : defaultConfirmationState;

    const treeWithDOI =
        isDoubleOptInFlagEnabled && effectiveConfirmationState === 'rsvp-yes'
            ? handleDoubleOptInMessage(baseTree, isDoubleOptInEnabled)
            : baseTree;

    const initialTree = adjustTreeForSessionRegistrations(treeWithDOI, sessionRegistrationConfirmation);

    if (request.isLoading) {
        return (
            <LoadingSpinnerContainer>
                <SuiSpinner loading size="large" aria-label="loading" />
            </LoadingSpinnerContainer>
        );
    }

    return (
        <Editor portal={portal} initialTree={initialTree} blocks={BLOCKS} Renderer={WebRenderer}>
            <TouchpointDataProvider event={event} guest={guest}>
                <React.Suspense fallback={null}>
                    <Portal portal={portal} />
                </React.Suspense>
            </TouchpointDataProvider>
        </Editor>
    );
}
